import { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft, MessageSquareText, Search, Trash2 } from 'lucide-react-native';
import { AppButton } from '../components/ui/AppButton';
import { AppCard } from '../components/ui/AppCard';
import { AppInput } from '../components/ui/AppInput';
import { AppText } from '../components/ui/AppText';
import { showAppToast } from '../components/ui/AppToast';
import { tokens } from '../theme/tokens';

const historyItems = [
  {
    id: 'geometry',
    title: '勾股定理专项讲解',
    preview: '整理了三角形边长关系和常见易错点。',
    timeLabel: '今天 11:41',
    modeLabel: '讲题',
  },
  {
    id: 'rift-valley',
    title: '东非大裂谷是怎样形成的？',
    preview: '解释板块张裂、断层下沉和陆地裂谷形成过程。',
    timeLabel: '昨天 18:20',
    modeLabel: '问答',
  },
  {
    id: 'paper',
    title: '数学周测试卷复盘',
    preview: '待接入试卷分析页后展示错题分布和薄弱知识点。',
    timeLabel: '6月13日',
    modeLabel: '试卷',
  },
];

export default function ConversationHistoryPage() {
  const router = useRouter();
  const [query, setQuery] = useState('');

  const filteredItems = useMemo(() => {
    const keyword = query.trim();
    if (!keyword) {
      return historyItems;
    }
    return historyItems.filter((item) => `${item.title}${item.preview}`.includes(keyword));
  }, [query]);

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <AppButton
          label="问答"
          variant="ghost"
          onPress={() => router.back()}
          leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
          style={styles.backButton}
        />
        <View style={styles.headerCopy}>
          <AppText variant="title" weight="bold">
            对话历史
          </AppText>
          <AppText variant="caption" tone="muted">
            搜索、继续或管理历史会话
          </AppText>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <AppCard style={styles.noticeCard}>
          <AppText variant="subtitle" weight="bold">
            对话历史待设计确认
          </AppText>
          <AppText variant="body" tone="muted">
            Figma 目前只有问答页里的“聊天记录”区块，没有独立历史列表。本页先补一期可点击列表和搜索骨架。
          </AppText>
        </AppCard>

        <View style={styles.searchWrap}>
          <Search size={18} color={tokens.colors.textSubtle} />
          <AppInput
            value={query}
            onChangeText={setQuery}
            placeholder="搜索历史对话"
            style={styles.searchInput}
          />
        </View>

        <View style={styles.list}>
          {filteredItems.map((item) => (
            <Pressable
              key={item.id}
              accessibilityRole="button"
              onPress={() => showAppToast('历史会话详情待设计确认', 'info')}
              style={({ pressed }) => [pressed ? styles.pressed : null]}
            >
              <AppCard style={styles.historyCard}>
                <View style={styles.historyIcon}>
                  <MessageSquareText size={20} color={tokens.colors.primary} />
                </View>
                <View style={styles.historyCopy}>
                  <View style={styles.historyTitleRow}>
                    <AppText variant="body" weight="bold" style={styles.historyTitle}>
                      {item.title}
                    </AppText>
                    <AppText variant="caption" tone="muted">
                      {item.timeLabel}
                    </AppText>
                  </View>
                  <AppText variant="caption" tone="muted">
                    {item.preview}
                  </AppText>
                  <View style={styles.historyFooter}>
                    <View style={styles.modePill}>
                      <AppText variant="caption" tone="primary" weight="bold">
                        {item.modeLabel}
                      </AppText>
                    </View>
                    <Pressable
                      accessibilityRole="button"
                      onPress={() => showAppToast('删除会话待设计确认', 'info')}
                      style={styles.deleteButton}
                    >
                      <Trash2 size={16} color={tokens.colors.textSubtle} />
                    </Pressable>
                  </View>
                </View>
              </AppCard>
            </Pressable>
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  noticeCard: {
    gap: tokens.spacing.sm,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  searchWrap: {
    minHeight: tokens.layout.inputHeight,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    backgroundColor: tokens.colors.surface,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingLeft: tokens.spacing.lg,
  },
  searchInput: {
    flex: 1,
    borderWidth: 0,
    backgroundColor: 'transparent',
    paddingHorizontal: 0,
  },
  list: {
    gap: tokens.spacing.md,
  },
  historyCard: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
  },
  historyIcon: {
    width: 42,
    height: 42,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  historyCopy: {
    flex: 1,
    gap: tokens.spacing.sm,
  },
  historyTitleRow: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
    alignItems: 'flex-start',
  },
  historyTitle: {
    flex: 1,
  },
  historyFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modePill: {
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  deleteButton: {
    width: 34,
    height: 34,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: {
    opacity: 0.86,
  },
});
